import React, { useEffect, useState } from "react";
import useStyles from "./payouts_page_styles";
import AppBar from "../../../components/app_bar/app_bar";
import RestaurantApiService from "../../../api_service/restaurant_api_service";
import {
  Button,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box } from "@mui/system";
import { addDays, format } from "date-fns";
import commaNumber from "comma-number";
import { Download } from "@mui/icons-material";
import { useStoreState } from "easy-peasy";
import { useHistory } from "react-router-dom";
import {
  Panels,
  PaymentModes,
  PayoutsStartDate,
  PayoutStatuses,
} from "../../../utils/constants";
import PayoutsFilter from "../../../components/payouts_filter/payouts_filter";
import RestaurantEditRestaurant from "../../../components/restaurant_details_modal/restaurant_details_modal";
import { getImageUrl } from "../../../utils/helpers";

export default function RestaurantPayoutsPage() {
  const classes = useStyles();
  const history = useHistory();
  const restaurant = useStoreState((state) => state.restaurant);
  const isPayoutsView = useStoreState(
    (state) => state.currentView === Panels.PAYOUTS
  );
  const [state, setState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalPages: 1,
  });

  const [filter, setFilter] = useState({
    from: format(PayoutsStartDate, "yyyy-MM-dd"),
    to: format(addDays(new Date(), 1), "yyyy-MM-dd"),
    status: -1,
  });

  useEffect(() => {
    RestaurantApiService()
      .getPayouts(
        {
          from_date: filter.from,
          to_date: filter.to,
          status: filter.status === -1 ? null : filter.status,
        },
        state.page,
        state.limit
      )
      .then((response) => {
        if (response.success) {
          setState({
            ...state,
            data: response.data.data,
            totalPages: response.data.total_pages,
          });
        }
      });
  }, [state.page, state.limit, filter]); // eslint-disable-line

  useEffect(() => {
    if (!isPayoutsView) {
      history.replace("/restaurant/home");
    }
    RestaurantApiService().getRestaurantCall();
  }, []); // eslint-disable-line

  const onFilterChanged = (key, value) => {
    setFilter({ ...filter, [key]: value });
  };

  return restaurant ? (
    <div className={classes.root}>
      <AppBar />
      <PayoutsFilter onFilterChanged={onFilterChanged} filter={filter} />
      <div className={classes.main_row}>
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell style={{ minWidth: 120 }} align="left">
                  From
                </TableCell>
                <TableCell style={{ minWidth: 120 }} align="left">
                  To
                </TableCell>
                <TableCell align="center">Currency</TableCell>
                <TableCell style={{ minWidth: 110 }} align="right">
                  Total Sales
                </TableCell>
                <TableCell style={{ minWidth: 160 }} align="center">
                  Fee Percentage (%)
                </TableCell>
                <TableCell style={{ minWidth: 130 }} align="right">
                  Restaurant Fee
                </TableCell>
                <TableCell style={{ minWidth: 140 }} align="right">
                  Application Fee
                </TableCell>
                <TableCell style={{ minWidth: 130 }} align="center">
                  Payout Status
                </TableCell>
                <TableCell style={{ minWidth: 120 }} align="center">
                  Payout Mode
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.data.map((payout) => (
                <Row key={payout.id} payout={payout} />
              ))}
            </TableBody>
          </Table>
          <div className={classes.paginator_outer}>
            {state.totalPages > 1 && (
              <Pagination
                count={state.totalPages}
                page={state.page}
                onChange={(event, newPage) => {
                  setState({ ...state, page: newPage });
                }}
              />
            )}
          </div>
        </TableContainer>
      </div>
      <RestaurantEditRestaurant />
    </div>
  ) : (
    <div />
  );
}

function Row({ payout }) {
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand payout"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="order">
          {format(new Date(payout.from_date), "yyyy MMM d")}
        </TableCell>
        <TableCell component="th" scope="order">
          {format(new Date(payout.to_date), "yyyy MMM d")}
        </TableCell>
        <TableCell align="center">{payout.currency}</TableCell>
        <TableCell align="right">
          {commaNumber(payout.total_amount.toFixed(2))}
        </TableCell>
        <TableCell align="center">{payout.app_fee_percentage}</TableCell>
        <TableCell align="right">
          {commaNumber(payout.restaurant_fee.toFixed(2))}
        </TableCell>
        <TableCell align="right">
          {commaNumber(payout.app_fee.toFixed(2))}
        </TableCell>
        <TableCell
          align="center"
          style={{
            color: payout.status === "PAID" ? "green" : "#2b7ab3",
            fontWeight: "bold",
          }}
        >
          {payout.status}
        </TableCell>
        <TableCell align="center">
          {payout.is_sandbox ? PaymentModes.SANDBOX : PaymentModes.PRODUCTION}
        </TableCell>
        <TableCell align="center">
          {payout.status === PayoutStatuses.PAID && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                window.open(getImageUrl(payout.receipt_url), "_blank");
              }}
            >
              <Tooltip title="Download payment receipt">
                <Download />
              </Tooltip>
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {`Orders included in the payout`}
              </Typography>
              <Box height={20} />
              <DetailRow
                title={"Initiated Date"}
                value={format(
                  new Date(payout.createdAt),
                  "yyyy MMM d - hh:mm aaa"
                )}
              />
              <DetailRow
                title={"Payment date"}
                value={
                  payout.payment_update_date
                    ? format(
                        new Date(payout.payment_update_date),
                        "yyyy MMM d - hh:mm aaa"
                      )
                    : "--"
                }
              />
              <DetailRow
                title={"Transfer Amount"}
                value={
                  <span style={{ color: "green" }}>
                    {commaNumber(payout.restaurant_fee.toFixed(2))}
                  </span>
                }
              />
              <DetailRow
                title={"Bank Details"}
                value={payout.bank_account_details?.split(",").map((b) => (
                  <>
                    <span>{b}</span>
                    <br />
                  </>
                ))}
              />
              {payout.comment && (
                <DetailRow title={"Comment"} value={payout.comment} />
              )}

              <Box height={20} />

              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Payment Order Id</TableCell>
                    <TableCell align="left">Customer</TableCell>
                    <TableCell align="left">Server</TableCell>
                    <TableCell align="center">Payment Method</TableCell>
                    <TableCell align="right">Order Amount</TableCell>
                    <TableCell align="right">Tip Amount</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payout.orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell align="left">
                        {order.payment_order_id}
                      </TableCell>
                      <TableCell align="left">
                        {order.customer_name || "---"}
                      </TableCell>
                      <TableCell align="left">
                        {order.waiter || "---"}
                      </TableCell>
                      <TableCell align="center">
                        {order.payment_method}
                      </TableCell>
                      <TableCell align="right">
                        {commaNumber(order.order_amount.toFixed(2))}
                      </TableCell>
                      <TableCell align="right">
                        {commaNumber(order.tip_amount.toFixed(2))}
                      </TableCell>
                      <TableCell align="right">
                        {commaNumber(
                          (order.order_amount + order.tip_amount).toFixed(2)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const DetailRow = ({ title, value }) => {
  const classes = useStyles();
  return (
    <div className={classes.detail_row}>
      <div className={classes.detail_label}>{title}</div>
      <div className={classes.detail_value}>{value}</div>
    </div>
  );
};
